import { forwardRef, ReactElement, Ref, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { ADD_COMPETITIONS } from 'utils/constants/tour';
import { isMobile } from 'utils/is-mobile';
import { addRegionNameToCompetition } from 'utils/sportsbook/add-region-name-to-competition';
import { ENotRealSports } from 'interfaces/not-real-sports';
import {
  ESportsbookLayoutTypes,
  ETopMatchesLayoutType
} from 'interfaces/sportsbook-configs';
import {
  Competition,
  Game,
  Region,
  Sport
} from 'interfaces/sportsbook-data-levels';
import { useCompetitionClick } from './hooks/useCompetitionClick';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { NavigationCollapse } from 'components/NavigationCollapse';
import { CompetitionNameWithTooltip } from 'components/Shared/CompetitionNameWithTooltip';
import { CompetitionsFavorite } from 'newcomponents/Shared/sportsbook/CompetitionsFavorite';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { getSportsSortingOption } from 'store/selectors/new-sportsbook';
import { getScrollToCompetitionId } from 'store/selectors/sport-data';
import './index.less';

type TSportLevelNavigationProps = {
  navItemsToRender: Sport[];
  category: string;
};

export const SportLevelNavigation = forwardRef(
  (
    { navItemsToRender, category }: TSportLevelNavigationProps,
    scrollToRef: Ref<HTMLDivElement>
  ): ReactElement => {
    const { params } = useSbRouteMatch();
    const scrollToCompetitionId = useSelector(getScrollToCompetitionId);
    const sportsSortingOption = useSelector(getSportsSortingOption);

    const competitionClickHandler = useCompetitionClick(category);
    const configs = useSportsbookConfigs();

    useScrollToTop('prematch-left-sidebar-wrapper', [params?.category]);

    const getIsDefaultActive = (sport: Sport, index: number) => {
      return (
        (!Number(SpringConfigs?.SPORT_COMPETITION_IS_COMPACT) &&
          index === 0 &&
          !params.sport &&
          !scrollToCompetitionId) ||
        (!!scrollToCompetitionId &&
          !!sport?.competition?.[scrollToCompetitionId.id]) ||
        params.sport === sport.alias
      );
    };

    const createIconAfterText = (
      compId: number,
      compName: string,
      regionAlias: string,
      sportAlias: string
    ) => {
      if (isMobile()) {
        return (
          <CompetitionsFavorite
            iconSize={EIconSize._16}
            competitionInfo={{
              id: compId,
              name: compName,
              regionAlias,
              sportAlias
            }}
          />
        );
      } else {
        return (
          <GlobalIcon
            size={EIconSize._16}
            name={regionAlias}
            lib="flags"
            className="navigationCollapseWrapper__iconAfterText"
          />
        );
      }
    };

    const createIconBeforeText = (
      compId: number,
      compName: string,
      regionAlias: string,
      sportAlias: string
    ) => {
      if (isMobile()) {
        return (
          <GlobalIcon
            size={isMobile() ? EIconSize._20 : EIconSize._24}
            name={regionAlias}
            lib="flags"
            className="navigationCollapseWrapper__iconBeforeText"
          />
        );
      } else {
        return (
          <CompetitionsFavorite
            iconSize={EIconSize._16}
            competitionInfo={{
              id: compId,
              name: compName,
              regionAlias: regionAlias,
              sportAlias
            }}
          />
        );
      }
    };

    useEffect(() => {
      const isTopMatches = params.category === ENotRealSports.topMatches;

      const shouldDisableFirstCompetitionSelect =
        isTopMatches &&
        SpringConfigs.TOP_MATCHES_LAYOUT === ETopMatchesLayoutType.compact &&
        configs.spbLayoutType === ESportsbookLayoutTypes.CLASSIC;

      if (
        !shouldDisableFirstCompetitionSelect &&
        params.category !== ENotRealSports.multiples &&
        navItemsToRender.length &&
        !isMobile()
      ) {
        const isTopLeague = params.category === ENotRealSports.topLeague;

        if (isTopLeague && orderedData?.length) {
          if (!params.region && !params.competition) {
            const competition = sortByOrder(
              Object.values(orderedData[0].competition || {}),
              'favorite_order'
            )[0];

            if (competition) {
              competitionClickHandler({
                sportAlias: orderedData[0].alias,
                regionAlias: String(competition.region),
                compId: competition.id
              });
            }
          }
        } else {
          const sportDataByRouteAlias = (navItemsToRender.length &&
            params.sport &&
            navItemsToRender?.find(
              sport => sport.alias === params.sport
            )) as Sport<Game>;

          const regionDataByRouteAlias = (sportDataByRouteAlias?.region &&
            params.region &&
            Object.values(sportDataByRouteAlias.region).find(
              reg => reg.alias === params.region
            )) as Region<Game>;

          const competitionDataByRouteAlias =
            regionDataByRouteAlias?.competition &&
            Object.values(regionDataByRouteAlias.competition).find(
              comp => +comp.id === +params.competition
            );

          const firstRegion = sortByOrder(
            Object.values(navItemsToRender[0].region || {})
          )[0];

          const firstCompetition = sortByOrder(
            Object.values(firstRegion?.competition || {}),
            isTopLeague ? 'favorite_order' : undefined
          )[0];

          const shouldRedirectToFirst =
            (firstCompetition &&
              !sportDataByRouteAlias &&
              !regionDataByRouteAlias &&
              !competitionDataByRouteAlias) ||
            !sportDataByRouteAlias ||
            !regionDataByRouteAlias ||
            !competitionDataByRouteAlias;

          if (shouldRedirectToFirst) {
            competitionClickHandler({
              sportAlias: navItemsToRender[0].alias,
              regionAlias: firstRegion.alias,
              compId: firstCompetition.id,
              forcePushUrl:
                !sportDataByRouteAlias ||
                !regionDataByRouteAlias ||
                !competitionDataByRouteAlias
            });
          }
        }
      }
    }, [params, navItemsToRender]);

    const ViewCollapse = useMemo(
      () => (isMobile() ? EventCollapse : NavigationCollapse),
      []
    );

    const orderedData = useMemo(() => {
      const orderedCompetitions = addRegionNameToCompetition(
        {
          sport: navItemsToRender.reduce(
            (acc, sport) => ({ ...acc, [sport.id]: { ...sport } }),
            {}
          )
        },
        category !== ENotRealSports.topLeague
      );

      if (sportsSortingOption.key && orderedCompetitions) {
        return sortByOrder(
          Object.values(orderedCompetitions),
          sportsSortingOption.key,
          sportsSortingOption.type
        );
      }

      return orderedCompetitions && Object.values(orderedCompetitions);
    }, [
      category,
      navItemsToRender,
      sportsSortingOption.key,
      sportsSortingOption.type
    ]);

    return (
      <div
        className={cc([
          'navigationCollapseWrapper',
          { 'navigationCollapseWrapper--mobile': isMobile() }
        ])}
      >
        {orderedData?.map((sport, index) => (
          <ViewCollapse
            dataTestId="sport"
            renderInDom={SpringConfigs.RENDER_COMPETITIONS_IN_DOM}
            wrapperBackColor="var(--v3-black-1)"
            title={sport.name}
            key={sport.id}
            icon={
              <GlobalIcon
                size={EIconSize._24}
                name={sport.alias}
                lib="sports"
              />
            }
            defaultActive={getIsDefaultActive(sport, index)}
          >
            <>
              {(category === ENotRealSports.topLeague
                ? sortByOrder(
                    Object.values(sport.competition || {}),
                    'favorite_order'
                  )
                : sortByOrder(Object.values(sport.region || {})).reduce(
                    (result: Competition[], region) => {
                      sortByOrder(
                        Object.values(region.competition || {})
                      ).forEach(competition => {
                        result.push({
                          ...competition,
                          regionId: region.id,
                          region: region.alias
                        });
                      });

                      return result;
                    },
                    []
                  )
              ).map((comp, index) => (
                <CompetitionNameWithTooltip
                  key={comp.id}
                  active={
                    category !== ENotRealSports.multiples &&
                    params.competition === String(comp.id)
                  }
                  dataTestId="competition"
                  competitionItem={comp}
                  {...(SpringConfigs.USER_PERSONALIZATION_ENABLED && index === 0
                    ? { 'data-tour': ADD_COMPETITIONS }
                    : {})}
                  clickHandler={e => {
                    competitionClickHandler({
                      sportAlias: sport.alias,
                      regionAlias: comp.region || '',
                      compId: comp.id,
                      e: e,
                      skipCategory: category === ENotRealSports.multiples
                    });
                  }}
                  competitionUrl={competitionClickHandler({
                    sportAlias: sport.alias,
                    regionAlias: comp.region || '',
                    compId: comp.id,
                    returnURLOnly: true
                  })}
                  IconAfterText={createIconAfterText(
                    comp.id,
                    comp.name,
                    comp.region || '',
                    sport.alias
                  )}
                  IconBeforeText={createIconBeforeText(
                    comp.id,
                    comp.name,
                    comp.region || '',
                    sport.alias
                  )}
                  {...(isMobile() &&
                    scrollToCompetitionId?.id === comp.id.toString() && {
                      ref: scrollToRef
                    })}
                />
              ))}
            </>
          </ViewCollapse>
        ))}
      </div>
    );
  }
);
